import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { remCalc } from '@monorepo/shared/src/util/style-util';
import { translate } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/util';
import {
  FONT_GSS_FUTURA_BOOK,
  FONT_GSS_FUTURA_SEMI_BOLD,
} from '@monorepo/shared/src/themes/defaultTheme';
import useReports from 'state/hooks/use-reports';
import useSkipAssignment from 'state/hooks/use-skip-assignment';
import { getProgressDataForStudentAndLetter } from '../../utils/reports-util';

const t = (key: string, options?) => translate('reports', key, options);

type StyleProps = {
  areAllAssignmentsCompleted: boolean;
  noAssignments: boolean;
  areAllAssignmentsSkipped: boolean;
};

const SkipAssignmentCard: React.FC = () => {
  const [{ progressData, selectedCharacter, selectedStudent }] = useReports();
  const [openSkipAssignmentModal, setOpenSkipAssignmentModal] = useState<boolean>(false);
  const [isUnskip, setIsUnskip] = useState<boolean>(false);

  const progress = getProgressDataForStudentAndLetter(
    progressData,
    selectedStudent?.id,
    selectedCharacter?.id
  );
  const areAllAssignmentsCompleted =
    progress?.activityCompletedCount === progress?.activityTotalCount;

  const areAllAssignmentsSkipped = progress?.activitySkippedCount >= progress?.activityTotalCount;

  const noAssignments = !progress;

  const classes = useStyles({
    areAllAssignmentsCompleted,
    noAssignments,
    areAllAssignmentsSkipped,
  });

  const openModal = () => {
    setIsUnskip(areAllAssignmentsSkipped);
    setOpenSkipAssignmentModal(true);
  };

  useSkipAssignment({
    student: selectedStudent,
    character: selectedCharacter?.character,
    openSkipAssignmentModal,
    setOpenSkipAssignmentModal,
    isUnskip,
  });

  return (
    <Box
      component={Paper}
      padding={6}
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      gap={remCalc(12)}
      className="noPrint"
    >
      <Box>
        <Typography className={classes.title}>
          {areAllAssignmentsSkipped ? t('unskipAssignment') : t('skipAssignment')}
        </Typography>
        <Typography className={classes.summary}>
          {areAllAssignmentsSkipped ? t('unskipCardSummary') : t('skipCardSummary')}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center">
        <Button
          color="primary"
          onClick={openModal}
          disabled={areAllAssignmentsCompleted && !areAllAssignmentsSkipped}
        >
          <Typography className={classes.buttonText}>
            {areAllAssignmentsSkipped ? t('unskipAssignment') : t('skipAssignment')}
          </Typography>
        </Button>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: remCalc(16),
    lineHeight: remCalc(24),
    marginBottom: remCalc(8),
    fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
    color: (props: StyleProps) =>
      (props.areAllAssignmentsCompleted || props.noAssignments) && !props.areAllAssignmentsSkipped
        ? theme.palette.grey[400]
        : theme.palette.common.black,
  },
  summary: {
    fontSize: remCalc(14),
    lineHeight: remCalc(20),
    fontFamily: FONT_GSS_FUTURA_BOOK,
    color: (props: StyleProps) =>
      (props.areAllAssignmentsCompleted || props.noAssignments) && !props.areAllAssignmentsSkipped
        ? theme.palette.grey[400]
        : theme.palette.common.black,
  },
  buttonText: {
    fontSize: remCalc(16),
    lineHeight: remCalc(24),
    fontFamily: FONT_GSS_FUTURA_BOOK,
  },
}));

export default SkipAssignmentCard;
